import { getAuthHeaders, REDIS_CONSOLE_API } from '@/api/client'
import type { RedisMetrics, RedisKeysPage, RedisKeyDetail } from '../types/redisConsole.types'

/** Local fetch wrapper (not the shared fetchApi) — the force-expire endpoint returns 204 No
 *  Content, which fetchApi's unconditional response.json() call cannot parse. Mirrors
 *  mongo-console/api/mongoConsole.ts's own fetchJson, which handles the same case for the same
 *  reason (its DELETE endpoint also returns 204). */
async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, { ...options, headers: { ...getAuthHeaders(), ...options?.headers } })
  if (!res.ok) {
    const text = await res.text()
    throw new Error(`API Error ${res.status}: ${text}`)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

export async function getRedisMetrics(): Promise<RedisMetrics> {
  return fetchJson<RedisMetrics>(`${REDIS_CONSOLE_API}/metrics`)
}

export async function searchRedisKeys(pattern: string, cursor: string, count = 100): Promise<RedisKeysPage> {
  const q = new URLSearchParams({ pattern, cursor, count: String(count) })
  return fetchJson<RedisKeysPage>(`${REDIS_CONSOLE_API}/keys?${q.toString()}`)
}

export async function getRedisKeyDetail(key: string): Promise<RedisKeyDetail> {
  const q = new URLSearchParams({ key })
  return fetchJson<RedisKeyDetail>(`${REDIS_CONSOLE_API}/key?${q.toString()}`)
}

/** Deletes the key immediately (force-expire). Audit-logged server-side with the given reason. */
export async function expireRedisKey(key: string, reason: string): Promise<void> {
  const q = new URLSearchParams({ key, reason })
  await fetchJson<void>(`${REDIS_CONSOLE_API}/key?${q.toString()}`, { method: 'DELETE' })
}
